import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Check, Shield, Zap, Building2 } from 'lucide-react';
import PublicNav from '../components/PublicNav';
import PublicFooter from '../components/PublicFooter';
import usePageMeta from '../utils/usePageMeta';
import { track, TRIAL_CTA, DEMO_CTA, PRICING_PLAN } from '../utils/analytics';

// ── Static fallback (used when /api/plans is unreachable) ────────────────────
// Must stay in sync with functions/shared/plans.js.
// The live source of truth is the /api/plans endpoint this is display-only.
const PLAN_STATIC_FALLBACK = [
  {
    id: 'coach',
    product: 'post_match',
    name: 'Coach',
    tagline: 'For individual coaches and analysts.',
    monthlyPrice: 99,
    annualPrice: 990,
    publicPriceLabel: null,
    badge: null,
    icon: Shield,
    ctaLabel: 'Start free trial',
    ctaTo: '/login?mode=signup&plan=coach',
    trialNote: '14-day free trial included',
    cardStyle: 'border-gray-200',
    btnStyle: 'bg-white border border-navy-900 text-navy-900 hover:bg-navy-50',
    limits: {
      users: '1 active user',
      teams: '1 team',
      reports: '8 reports per billing month',
      support: 'Standard email support',
    },
    features: [
      'Full Tactmark coaching report',
      'Key-moment paste and file import',
      'Prioritized coaching findings',
      'Training-focus recommendations',
      'Player and role reviews',
      'Match evidence',
      'Detailed coaching observations',
      'Report history',
      'Recurring pattern tracking',
      'Read-only report sharing',
    ],
    selfServe: true,
  },
  {
    id: 'club',
    product: 'post_match',
    name: 'Club',
    tagline: 'For clubs, academies, and coaching staff.',
    monthlyPrice: 399,
    annualPrice: 3990,
    publicPriceLabel: null,
    badge: 'Most popular',
    icon: Zap,
    ctaLabel: 'Start free trial',
    ctaTo: '/login?mode=signup&plan=club',
    trialNote: '14-day free trial included',
    cardStyle: 'border-navy-900 ring-1 ring-navy-900',
    btnStyle: 'bg-navy-900 hover:bg-navy-800 text-white',
    limits: {
      users: 'Up to 5 active users',
      teams: '1 team',
      reports: '30 reports per billing month',
      support: 'Priority email support',
    },
    features: [
      'Full Coach plan capabilities',
      'Owner, Staff, and Viewer roles',
      'User invitations',
      'Shared team workspace',
      'Team-access assignment',
      'Player and role reviews',
      'Match evidence',
      'Detailed coaching observations',
      'Report sharing',
      'Recurring pattern tracking',
      'Priority support',
    ],
    selfServe: true,
  },
  {
    id: 'enterprise',
    product: 'federation',
    name: 'Federation',
    tagline: 'For football associations and multi-program organizations that need technical-development oversight and organization-level reporting.',
    monthlyPrice: null,
    annualPrice: null,
    publicPriceLabel: 'Custom annual agreement',
    badge: null,
    icon: Building2,
    ctaLabel: 'Book a Federation Demo',
    ctaTo: '/contact?plan=federation&from=pricing',
    trialNote: null,
    cardStyle: 'border-gray-200',
    btnStyle: 'bg-white border border-navy-900 text-navy-900 hover:bg-navy-50',
    limits: {
      users: 'Contract-defined access',
      teams: 'Multiple programs and participating teams',
      reports: 'Organization-level development reporting',
      support: 'Guided implementation and contract-defined support',
    },
    features: [
      'Multiple programs and participating teams',
      'Development objectives',
      'Standardized reporting periods and templates',
      'Organizational action tracking',
      'Technical-leadership oversight',
      'Development evidence reporting',
      'Guided implementation',
      'Contract-defined access and support',
    ],
    selfServe: false,
  },
];

const PLAN_META = {
  coach:      { icon: Shield, cardStyle: 'border-gray-200', btnStyle: 'bg-white border border-navy-900 text-navy-900 hover:bg-navy-50', ctaLabel: 'Start free trial', ctaTo: '/login?mode=signup&plan=coach' },
  club:       { icon: Zap,      cardStyle: 'border-navy-900 ring-1 ring-navy-900', btnStyle: 'bg-navy-900 hover:bg-navy-800 text-white', ctaLabel: 'Start free trial', ctaTo: '/login?mode=signup&plan=club' },
  enterprise: { icon: Building2, cardStyle: 'border-gray-200', btnStyle: 'bg-white border border-navy-900 text-navy-900 hover:bg-navy-50', ctaLabel: 'Book a Federation Demo', ctaTo: '/contact?plan=federation&from=pricing', publicPriceLabel: 'Custom annual agreement', name: 'Federation' },
};

function mergePlanData(apiPlans) {
  // Map API data into display-ready objects, preserving static UI metadata
  return ['coach', 'club', 'enterprise'].map((id) => {
    const api = apiPlans.find((p) => p.plan_id === id);
    const stat = PLAN_STATIC_FALLBACK.find((p) => p.id === id);
    const meta = PLAN_META[id];
    if (!api || !stat) return stat; // fallback
    return {
      ...stat,
      ...meta,
      monthlyPrice: api.monthly_price_usd ?? stat.monthlyPrice,
      annualPrice:  api.annual_price_usd  ?? stat.annualPrice,
      publicPriceLabel: meta.publicPriceLabel ?? stat.publicPriceLabel ?? null,
      name: meta.name || stat.name,
      selfServe: api.self_serve ?? stat.selfServe,
      product: api.product || stat.product,
      limits: {
        users:   api.max_users   ? `${api.max_users === 1 ? '1 active user' : `Up to ${api.max_users} active users`}` : stat.limits.users,
        teams:   api.max_teams   ? `${api.max_teams === 1 ? '1 team' : `${api.max_teams} teams`}` : stat.limits.teams,
        reports: api.reports_per_billing_month ? `${api.reports_per_billing_month} reports per billing month` : stat.limits.reports,
        support: api.support_level === 'priority' ? 'Priority email support' : api.support_level === 'contract_defined' ? 'Priority support' : 'Standard email support',
      },
      trialNote: api.trial_days > 0 ? `${api.trial_days}-day free trial included` : null,
    };
  });
}

const FAQS = [
  {
    q: 'Does Tactmark watch or analyze match footage automatically?',
    a: 'No. Tactmark does not automatically watch or analyze full match footage. Reports are built from match context, coach-entered key moments, coach observations, tactical focus, and player and role reviews. Footage can be attached as a reference alongside the report.',
  },
  {
    q: 'How are coaching reports generated?',
    a: 'The Tactmark Analysis Engine computes structured findings first key-moment scores, team perspective, scoreline context, tactical weighting, risk scores, recurring patterns, and coaching-decision signals. The final report is then written from those findings.',
  },
  {
    q: 'What does the report limit count?',
    a: 'Only successfully generated reports count toward the monthly allowance. Draft matches, failed attempts, import previews, and cancelled requests do not count. Usage resets each billing cycle.',
  },
  {
    q: 'Can I share reports with staff?',
    a: 'Yes. Share a read-only report link with coaches and analysts, or export to PDF.',
  },
  {
    q: 'Is there a free trial?',
    a: 'Coach and Club plans include a 14-day free trial for Tactmark Post-Match. Federation deployments are custom and begin with a Federation demo.',
  },
  {
    q: 'What is included in Federation?',
    a: 'Federation is a separate custom product for football associations and multi-program organizations. Scope is configured around development objectives, programs, reporting standards, accountability, staff access, and support.',
  },
  {
    q: 'Do we need Tactmark Post-Match to use Federation?',
    a: 'No. Federation supports its own development objectives, programs, reporting periods, organizational priorities, actions, reviews, and reports. Post-Match can be connected later as an optional integration source.',
  },
  {
    q: 'Can I cancel anytime?',
    a: 'Coach and Club self-serve subscriptions can be cancelled from account settings. Access remains active through the end of the paid period. Federation agreements are custom contracts and are not cancelled from the self-serve subscription screen.',
  },
];

export default function Pricing() {
  const [annual, setAnnual] = useState(false);
  const [plans, setPlans] = useState(PLAN_STATIC_FALLBACK);

  // Fetch live plan data from canonical server endpoint
  useEffect(() => {
    fetch('/api/plans')
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        if (data?.plans?.length) {
          setPlans(mergePlanData(data.plans));
        }
      })
      .catch(() => { /* keep static fallback */ });
  }, []);

  usePageMeta(
    'Tactmark Pricing | Post-Match Plans and Federation Agreements',
    'Compare Coach and Club subscriptions for Tactmark Post-Match, or book a demo for a custom annual Tactmark Federation agreement.'
  );

  return (
    <div className="min-h-screen bg-white">
      <PublicNav />

      {/* Hero */}
      <div className="max-w-5xl mx-auto px-6 pt-16 pb-4 text-center">
        <p className="text-navy-600 text-xs font-semibold tracking-widest uppercase mb-4">Pricing</p>
        <h1 className="font-serif text-4xl sm:text-5xl text-navy-900 leading-tight">
          Pricing for two focused football products.
        </h1>
        <p className="text-gray-500 text-lg mt-5 max-w-xl mx-auto">
          Choose a Post-Match subscription for team-level match review or discuss a custom Federation agreement for organization-level technical development.
        </p>
        <p className="text-navy-600 text-sm mt-3 font-medium">
          Coach and Club include a 14-day free trial for Tactmark Post-Match. Federation pricing is custom.
        </p>
        <p className="text-sm mt-2">
          <Link to="/products" className="text-navy-900 font-medium hover:underline">View products</Link>
        </p>

        {/* Monthly / Annual toggle only shown for self-serve plans */}
        <div className="flex items-center justify-center gap-3 mt-8">
          <span className={`text-sm font-medium ${!annual ? 'text-navy-900' : 'text-gray-400'}`}>Monthly</span>
          <button
            onClick={() => setAnnual((a) => !a)}
            aria-pressed={annual}
            aria-label="Toggle annual billing"
            className={`relative w-11 h-6 rounded-full transition-colors ${annual ? 'bg-navy-900' : 'bg-gray-200'}`}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${annual ? 'translate-x-5' : ''}`}
            />
          </button>
          <span className={`text-sm font-medium ${annual ? 'text-navy-900' : 'text-gray-400'}`}>
            Annual <span className="text-green-600 font-semibold">(save ~17%)</span>
          </span>
        </div>
      </div>

      {/* Post-Match plans */}
      <section className="max-w-5xl mx-auto px-6 pt-12">
        <p className="text-navy-600 text-xs font-semibold tracking-widest uppercase mb-3">Tactmark Post-Match</p>
        <h2 className="font-serif text-3xl text-navy-900 mb-3">Subscriptions for coaching teams</h2>
        <p className="text-gray-500 max-w-2xl">Coach and Club are Post-Match plans for reviewing completed matches and turning findings into coaching responses.</p>
      </section>
      <div className="max-w-5xl mx-auto px-6 py-8 grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
        {plans.filter((plan) => plan.product === 'post_match').map((plan) => {
          const Icon = plan.icon;
          const showAnnualPrice = annual && plan.annualPrice !== null && plan.selfServe;

          return (
            <div
              key={plan.id}
              className={`bg-white rounded-xl border ${plan.cardStyle} p-7 flex flex-col relative`}
            >
              {plan.badge && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                  <span className="bg-navy-900 text-white text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">
                    {plan.badge}
                  </span>
                </div>
              )}

              <Icon className="w-5 h-5 text-navy-700 mb-4 mt-1" />
              <h2 className="text-navy-900 font-semibold text-lg mb-1">{plan.name}</h2>
              <p className="text-gray-500 text-sm mb-4 leading-relaxed">{plan.tagline}</p>

              {/* Price */}
              <div className="mb-1 min-h-[3rem] flex items-end gap-1">
                {plan.publicPriceLabel ? (
                  <span className="text-4xl font-bold text-navy-900">{plan.publicPriceLabel}</span>
                ) : showAnnualPrice ? (
                  <>
                    <span className="text-4xl font-bold text-navy-900">${plan.annualPrice}</span>
                    <span className="text-gray-500 mb-1">/year</span>
                  </>
                ) : (
                  <>
                    <span className="text-4xl font-bold text-navy-900">${plan.monthlyPrice}</span>
                    <span className="text-gray-500 mb-1">/month</span>
                  </>
                )}
              </div>

              {/* Trial note only for self-serve plans with a trial */}
              <p className="text-navy-600 text-xs font-medium mb-5 min-h-[1.25rem]">
                {plan.trialNote || ''}
              </p>

              {/* Limits block */}
              <div className="bg-gray-50 rounded-lg px-4 py-3 mb-5 space-y-1.5">
                {Object.values(plan.limits).map((val) => (
                  <p key={val} className="text-navy-800 text-xs font-medium">{val}</p>
                ))}
              </div>

              {/* Feature list */}
              <ul className="space-y-2.5 flex-1 mb-6">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-gray-700">
                    <Check className="text-navy-700 w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <Link
                to={plan.ctaTo}
                onClick={() => {
                  track(PRICING_PLAN, { plan: plan.id });
                  track(TRIAL_CTA, { plan: plan.id, from: 'pricing' });
                }}
                className={`text-center font-medium py-3 rounded transition-colors text-sm ${plan.btnStyle}`}
              >
                {plan.ctaLabel}
              </Link>
            </div>
          );
        })}
      </div>

      {/* Federation agreement */}
      <section className="max-w-5xl mx-auto px-6 py-8 border-t border-gray-200">
        <p className="text-navy-600 text-xs font-semibold tracking-widest uppercase mb-3">Tactmark Federation</p>
        <h2 className="font-serif text-3xl text-navy-900 mb-3">Custom annual agreement</h2>
        <p className="text-gray-500 max-w-3xl mb-8">Federation is contracted separately from Post-Match and configured around programs, reporting standards, organizational responsibilities, access, implementation, and support.</p>
        {plans.filter((plan) => plan.product === 'federation').map((plan) => (
          <div key={plan.id} className="bg-white rounded-xl border border-gray-200 p-7 grid md:grid-cols-2 gap-8">
            <div>
              <Building2 className="w-5 h-5 text-navy-700 mb-4" />
              <h3 className="text-navy-900 font-semibold text-xl mb-2">Tactmark Federation</h3>
              <p className="text-2xl font-bold text-navy-900 mb-3">{plan.publicPriceLabel}</p>
              <p className="text-gray-500 text-sm leading-relaxed mb-6">{plan.tagline}</p>
              <Link to={plan.ctaTo} onClick={() => { track(PRICING_PLAN, { product: 'federation' }); track(DEMO_CTA, { plan: 'federation', from: 'pricing' }); }} className="inline-block bg-navy-900 hover:bg-navy-800 text-white font-medium px-6 py-3 rounded transition-colors">Book a Federation Demo</Link>
            </div>
            <ul className="space-y-2.5">
              {plan.features.map((feature) => (
                <li key={feature} className="flex items-start gap-2 text-sm text-gray-700">
                  <Check className="text-navy-700 w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </section>

      {/* Footnote */}
      <p className="text-center text-gray-400 text-xs px-6 -mt-4 mb-10 max-w-3xl mx-auto leading-relaxed">
        All prices are in U.S. dollars. Taxes may apply. Usage limits reset according to the subscription billing
        cycle. Federation agreements are quoted according to participating teams, staff access, onboarding, support,
        and organizational requirements.
      </p>

      {/* FAQ */}
      <div className="max-w-2xl mx-auto px-6 pb-20">
        <h2 className="text-navy-900 font-semibold text-xl mb-8 text-center">Frequently asked questions</h2>
        <div className="space-y-6">
          {FAQS.map((faq) => (
            <div key={faq.q} className="border-b border-gray-100 pb-6">
              <p className="text-navy-900 font-medium mb-2">{faq.q}</p>
              <p className="text-gray-500 text-sm leading-relaxed">{faq.a}</p>
            </div>
          ))}
        </div>
      </div>

      <PublicFooter />
    </div>
  );
}
